import React, { useState, useEffect } from "react";
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import de from 'date-fns/locale/de';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendarpage.scss';

const locales = {
  'de': de,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const CustomAgendaDate = ({ day, label }) => {
  const ref = React.useRef(null);
  const today = new Date();

  const isToday =
    day.getDate() === today.getDate() &&
    day.getMonth() === today.getMonth() &&
    day.getFullYear() === today.getFullYear();

  React.useEffect(() => {
    if (isToday && ref.current) {
      const parentRow = ref.current.closest("tr");
      if (!parentRow) return;

      parentRow.classList.add("rbc-today-row");

      const dateCell = parentRow.querySelector(".rbc-agenda-date-cell");
      const rowspan = parseInt(dateCell?.getAttribute("rowspan") || "1", 10);

      let currentRow = parentRow.nextElementSibling;
      for (let i = 1; i < rowspan; i++) {
        if (currentRow) {
          currentRow.classList.add("rbc-today-row");
          currentRow = currentRow.nextElementSibling;
        }
      }
    }
  }, [isToday]);

  return (
    <span ref={ref} className={isToday ? "agenda-today-mark" : ""}>
      {label}
    </span>
  );
};

const MyCalendar = ({ events }) => {

  const eventStyleGetter = (event) => {
    const nowMs = new Date().getTime();

    const startTime = event.resource?.startsAt ?? new Date(event.start).getTime();
    const endTime   = event.resource?.endsAt ?? new Date(event.end).getTime();

    const isPast   = endTime < nowMs;
    const isFuture = startTime > nowMs;
    const isActive = startTime <= nowMs && endTime >= nowMs;

    let timeClass = "";
    if (isPast)   timeClass = "rbc-event-past";
    if (isActive) timeClass = "rbc-event-active-live";
    if (isFuture) timeClass = "rbc-event-future";

    const rateGroup = event.resource?.rateGroup;
    let typeClass = "rbc-event-default";

    if (rateGroup === "xp")       typeClass = "rbc-event-xp";
    if (rateGroup === "taming")   typeClass = "rbc-event-taming";
    if (rateGroup === "breeding") typeClass = "rbc-event-breeding";
    if (rateGroup === "nexcoins") typeClass = "rbc-event-nexcoins";
    if (rateGroup === "custom")   typeClass = "rbc-event-custom";

    return { 
      className: `${timeClass} ${typeClass}`.trim() 
    };
  };

  return (
    <div className="calendar-container">
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
				alldayAccessor={() => false}
        culture="de"
				showMultiDayTimes={true}
        eventPropGetter={eventStyleGetter}
        components={{
          agenda: {
            date: CustomAgendaDate,
          },
        }}
        dayPropGetter={(date) => {
          const now = new Date();
          const isToday =
            date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear();

          return isToday ? { className: 'rbc-today-cell' } : {};
        }}
        formats={{
          dayFormat: (date, culture, localizer) =>
            localizer.format(date, 'EEEEEEE, dd.', culture),

          agendaDateFormat: (date, culture, localizer) =>
            localizer.format(date, 'EEEEEEE, dd. MMM', culture),

          agendaHeaderFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'dd. MMM', culture)} - ${localizer.format(end, 'dd. MMM yyyy', culture)}`,

          dayHeaderFormat: (date, culture, localizer) =>
            localizer.format(date, 'EEEE, dd. MMMM yyyy', culture),

          dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'dd. MMM', culture)} - ${localizer.format(end, 'dd. MMM yyyy', culture)}`,
        }}
        messages={{
          next: "Weiter",
          previous: "Zurück",
          today: "Heute",
          month: "Monat",
          week: "Woche",
          day: "Tag",
          agenda: "Agenda",
          date: "Datum",
          time: "Uhrzeit",
          event: "Ereignis",
          allDay: "Ganztägig",
          noEventsInRange: "Keine Termine in diesem Zeitraum.",
        }}
      />
    </div>
  );
};

const Calendarpage = () => {
  const [events, setEvents] = useState([]);

	useEffect(() => {
		const fetchEvents = async () => {
			try {
				const response = await fetch("https://www.nexark.de/proxy.php?mode=events");
				
				if (!response.ok) throw new Error(`HTTP Fehler: ${response.status}`);

				const data = await response.json();

				console.log("[Calendar] Events geladen:", data);
				
				const eventsObj = data.events || {};
				const rawEvents = Object.values(eventsObj);

				const formattedEvents = rawEvents.map(evt => {
					const startDate = new Date(evt.startsAt);
					let endDate = new Date(evt.endsAt);

					const durationMs = endDate.getTime() - startDate.getTime();
					const is24HoursMultiple = durationMs > 0 && durationMs % (24 * 60 * 60 * 1000) === 0;

					if (is24HoursMultiple && !evt.isAllDay) {
						endDate = new Date(endDate.getTime() - 1);
					}

					return {
						id: evt.id,
						title: evt.title + (
							evt.status === "fundraising" ? ` (Fundraising: ${evt.raisedCoins}/${evt.targetCoins})` :
							evt.status === "cancelled" ? " (Abgesagt)" :
							evt.status === "ended" ? " (Beendet)" :
							(evt.status === "scheduled" && evt.targetCoins > 0 ? " (Fundraising: erfolgreich)" : "")
						),
						start: startDate,
						end: endDate,
						allDay: false,
						resource: evt
					};
				});

				setEvents(formattedEvents);
			} catch (error) {
				console.error("[Calendar] Fehler beim Laden der Events über Proxy:", error);
			}
		};

		fetchEvents();

		const ONE_HOUR_MS = 60 * 60 * 1000;
		const intervalId = setInterval(fetchEvents, ONE_HOUR_MS);

		return () => clearInterval(intervalId);
	}, []);

  return (
    <div className='main-wrapper calendarpage-wrapper'>
      <MyCalendar events={events} />
    </div>
  );
};

export default Calendarpage;